import React, { useEffect, useRef, useState } from 'react'
import './create-question-set.styles.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faInfo, faInfoCircle, faList, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import NewQuestionForm from './new-question-form.component';
import Modal, { ModalAlert } from '@/components/SHARED/modal/modal.component';
import { properCase } from '@/utils/properCase';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useNavigate, useParams } from 'react-router-dom';
import ThreeDotsLoader from '@/components/SHARED/loader/three-dots-loader.component';
import NewInfoForm from './new-info-form.component';
import { faFileText } from '@fortawesome/free-regular-svg-icons';

const CreateQuestionSet = ({
  setAlertMessage,
  saveQuestionSet,
  loading,
  approveRef,
  questionSets
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [formData, setFormData] = useState({});
  const [createQuestionOpen, setCreateQuestionOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(null);
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const { setId } = useParams();
  const dataRef = useRef({});
  const navigate = useNavigate();
  let dataListener;

  useEffect(() => {
    return () => {
      if (dataListener) dataListener();
    }
  }, [])

  useEffect(() => {
    if (!setId || !questionSets) return;
    const setToEdit = questionSets[setId];
    const timeout = setTimeout(() => {
      if (Object.values(dataRef.current).length == 0) {
        setAlertMessage('There is no question set data to edit.');
        clearTimeout(timeout);
        navigate(-1);
        return;
      }
    }, 1000);
    dataRef.current = { ...setToEdit };
    setFormData(dataRef.current);
  }, [setId, questionSets])

  function saveData(key, value) {
    // console.log('Save Data', key, value);
    dataRef.current = {
      ...dataRef.current,
      ...{ [key]: value }
    }

    setFormData(dataRef.current);
    // console.log(dataRef.current);
    if (key === 'questions') {
      setCreateQuestionOpen(false);
    }
    setQuestionToEdit(null);
  }

  function moveQuestion(direction, questionId) {
    const currentPosition = formData.questions[questionId].order;
    const nextPosition = direction === "up" ? currentPosition - 1 : currentPosition + 1;
    const tempQuestions = { ...formData.questions };
    const questionToMove = Object.values(tempQuestions).find(q => q.order === currentPosition);
    const swapQuestion = Object.values(tempQuestions).find(q => q.order === nextPosition);
    questionToMove.order = nextPosition;
    swapQuestion.order = currentPosition;
    tempQuestions[questionToMove.id] = questionToMove;
    tempQuestions[swapQuestion.id] = swapQuestion;
    saveData('questions', tempQuestions);
  }

  function removeQuestion(questionId) {
    const tempQuestions = { ...formData.questions };
    delete tempQuestions[questionId];
    // Reorder remaining questions
    const reorderedQuestions = {};
    Object.values(tempQuestions)
      .sort((a, b) => a.order > b.order ? 1 : -1)
      .forEach((q, idx) => {
        q.order = idx + 1;
        reorderedQuestions[q.id] = q;
      });
    saveData('questions', reorderedQuestions);
  }



  function requestFormReset() {
    dataRef.current = {};
    setFormData(dataRef.current);
  }

  function editQuestion(question) {
    setCreateQuestionOpen(question.type === 'info' ? 'info' : 'question');
    console.log('Edit Question', question.id);
    setQuestionToEdit(question);
  }

  return (
    <div className='form-builder_create-question-set'>
      <h3>{
        setId
          ? 'Edit Question Set'
          : 'Create a New Question Set'
      }</h3>
      <div className="g-space-1"></div>
      <div className='form-builder_field small'>
        <label>Question Set Internal Name</label>
        <div className='meta'>(For internal use only)</div>
        <div className="g-space-0-5"></div>
        <input
          type="text"
          value={formData.name || ''}
          onChange={e => saveData('name', e.target.value)}
        />
      </div>
      <div className='form-builder_field small'>
        <label>Question Set Display Name</label>
        <div className='meta'>(What the users will see)</div>
        <div className="g-space-0-5"></div>
        <input
          type="text"
          value={formData.displayName || ''}
          onChange={e => saveData('displayName', e.target.value)}
        />
      </div>

      <div className='form-builder_buttons'>

        <button
          className='form-builder_button med-btn'
          onClick={() => setCreateQuestionOpen('info')}>
          Add Information
          <FontAwesomeIcon icon={faFileText} />
        </button>
        <button
          className='form-builder_button med-btn'
          onClick={() => setCreateQuestionOpen('question')}>
          Add Question
          <FontAwesomeIcon icon={faPlus} />
        </button>

      </div>
      <div className={`form-builder_create-question-card ${createQuestionOpen ? 'open' : ''}`}>
        <div className='card-content'>
          {
            createQuestionOpen === 'info'
              ?
              <NewInfoForm
                questionSetData={formData}
                saveSetData={saveData}
                setAlertMessage={setAlertMessage}
                informationToEdit={questionToEdit}
                closeForm={() => setCreateQuestionOpen(false)} />
              :
              <NewQuestionForm
                questionSetData={formData}
                setShowInfoModal={setShowInfoModal}
                saveSetData={saveData}
                setCreateQuestionOpen={setCreateQuestionOpen}
                questionToEdit={questionToEdit}
                setAlertMessage={setAlertMessage} />
          }
        </div>
      </div>
      <div className='form-builder_question-list-container'>
        Questions Added
        <hr />
        <div className='form-builder_question-list'>
          {
            formData.questions && Object.values(formData.questions).length > 0
              ? Object.values(formData.questions)
                .sort((a, b) => a.order > b.order ? 1 : -1)
                .map((question, idx) => (
                  <div key={question.id} className='question-item'>
                    <div>{question.order}.</div>
                    <div className='up-down-buttons'>
                      <button
                        className={`form-builder_button text-only no-pad ${idx === 0 ? 'disabled' : ''}`}
                        onClick={() => moveQuestion("up", question.id)}>
                        <FontAwesomeIcon
                          icon={faArrowUp} />
                      </button>
                      <button
                        className={`form-builder_button text-only no-pad ${idx === (Object.values(formData.questions).length - 1)
                            ? 'disabled'
                            : ''
                          }`
                        }
                        onClick={() => moveQuestion("down", question.id)}>
                        <FontAwesomeIcon
                          icon={faArrowDown} />
                      </button>

                    </div>
                    <div className='question-prompt'>
                      {
                        question.prompt.length > 60
                          ? question.prompt.slice(0, 60) + '...'
                          : question.prompt
                      }
                    </div>
                    <div className='meta'>
                      {properCase(question.type.replaceAll('_', ' '))}
                    </div>
                    {
                      (question.options)
                        ?
                        <div className='meta'>
                          Options: {question.options ? question.options.length : 0}
                        </div>
                        :
                        <div></div>
                    }
                    <div className='buttons'>
                      <button
                        className={`form-builder_button text-only no-pad`}
                        onClick={() => editQuestion(question)}>
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className={`form-builder_button text-only no-pad`}
                        onClick={() => removeQuestion(question.id)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))
              : <div className='meta'>No Questions Added</div>
          }
        </div>
        {
          (loading)
            ?
            <div className='form-builder_buttons'>
              <button className='form-builder_button'>
                Cancel
              </button>
              <button className='form-builder_button'>
                <ThreeDotsLoader />
              </button>
            </div>
            :
            <div className='form-builder_buttons'>
              <button
                className='form-builder_button'
                onClick={() => navigate(-1)} >
                Cancel
              </button>
              <button
                className='form-builder_button primary'
                onClick={() => saveQuestionSet(dataRef.current, true)} >
                Save Question Set
              </button>
            </div>
        }
      </div>
      <Modal
        show={showInfoModal}
        cancel={() => setShowInfoModal(null)}
        closeButton={false}
        cls="narrow"
        text={
          showInfoModal === 'comparisonQuestions'
            ? 'Coach/Protege Comparison Questions Info'
            : showInfoModal === 'questionTypes'
              ? 'Question Types Info'
              : showInfoModal === 'shortTitle'
                ? 'Short Title Info'
                : showInfoModal === 'comparisonWeight'
                  ? 'Comparison Weight Info'
                  : showInfoModal === 'requiredQuestion'
                    ? 'Required Question Info'
                    : ''
        }
      >
        <div className='form-builder_modal-content'>
          {
            showInfoModal === 'comparisonQuestions' &&
            (
              <>
                <p>
                  This toggle enables or disables the Coach/Protege
                  Comparison Questions. If toggled on, only multiple-choice questions
                  and checkbox questions are allowed. These questions will appear on both
                  the Coach and Protege applications, allowing managers to
                  compare responses and make informed decisions when matching
                  candidates.
                </p>

                <p>
                  When matching Coaches and Proteges, applications with similar answers
                  will be prioritized to ensure a better alignment of skills and experiences.
                </p>
                <p>
                  NOTE: Question sets that are designated as comparison questions cannot
                  also be used as Required Questions for All Applicants.
                </p>
              </>
            )
          }
          {
            showInfoModal === 'questionTypes' &&
            (
              <>
                <p>
                  There are four question types available:
                </p>
                <ul>
                  <li><strong>Short Answer:</strong> Allows applicants to provide
                    brief text responses.</li>
                  <li><strong>Long Answer:</strong> Enables more detailed responses
                    with paragraph-style text.</li>
                  <li><strong>Multiple Choice:</strong> Presents a list of options
                    for applicants to choose from.</li>
                  <li><strong>Checkbox:</strong> Allows applicants to select
                    multiple options from a list.</li>
                </ul>
              </>
            )
          }
          {
            showInfoModal === 'shortTitle' &&
            (
              <p>
                The Short Title is a brief, descriptive label for the question.
                It should be concise and clearly convey the main topic or focus
                of the question. This will be replace the prompt when viewing
                application responses.
              </p>
            )
          }
          {
            showInfoModal === 'comparisonWeight' &&
            (
              <p>
                The Comparison Weight determines the importance of the question
                in the overall assessment. A higher weight indicates a more
                critical question, and more weight will be given to it when comparing
                proteges with mentors.
              </p>
            )
          }
          {
            showInfoModal === 'requiredQuestion' &&
            (
              <p>
                A Required Question must be answered by the applicant. This
                ensures that key information is collected for all candidates.
              </p>
            )
          }
          {
            showInfoModal === 'requiredForAllQuestions' &&
            (
              <>
                <p>
                  Required Questions for all Applicants must be answered by every applicant,
                  regardless of their role or type. This ensures that essential information
                  is collected uniformly across all candidates.
                </p>
                <p>
                  NOTE: Question sets that are required
                  for all applicants cannot also be used for comparison questions.
                </p>
              </>
            )
          }
          <div className="form-builder_buttons">
            <button
              className='form-builder_button med-btn'
              onClick={() => setShowInfoModal(null)}>
              OK
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default CreateQuestionSet